// src/components/JsonToCsvConverter.js

import React, { useState, useEffect, useRef } from "react";
import {
  FaUpload,
  FaCopy,
  FaFileExport,
  FaArrowUp,
  FaArrowDown,
  FaMoon,
  FaSun,
} from "react-icons/fa";

const JsonToCsvConverter = () => {
  // Basic States
  const [jsonInput, setJsonInput] = useState("");
  const [csvOutput, setCsvOutput] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Line Numbers
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  // Advanced Options
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [delimiter, setDelimiter] = useState(",");
  const [ignoreNullFields, setIgnoreNullFields] = useState(false);

  // Example JSON
  const exampleJson = `{
  "company": "SFDC247",
  "employees": [
    {
      "id": 1,
      "name": "John Doe",
      "role": "Salesforce Developer",
      "projects": ["Project Alpha", "Project Beta"]
    },
    {
      "id": 2,
      "name": "Jane Smith",
      "role": "Salesforce Architect",
      "projects": ["Project Gamma"]
    }
  ]
}`;

  // Dark Mode side-effect
  useEffect(() => {
    if (darkMode) {
      document.body.style.backgroundColor = "#2d2d2d";
      document.body.style.color = "#f0f0f0";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }
  }, [darkMode]);

  // Flatten logic
  const flattenObject = (obj, prefix = "") =>
    Object.keys(obj).reduce((acc, key) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const val = obj[key];
      if (val === null && ignoreNullFields) {
        return acc; // skip null
      }
      if (typeof val === "object" && val !== null && !Array.isArray(val)) {
        Object.assign(acc, flattenObject(val, fullKey));
      } else {
        acc[fullKey] = Array.isArray(val) ? JSON.stringify(val) : val;
      }
      return acc;
    }, {});

  const jsonToCsv = (jsonData) => {
    if (!Array.isArray(jsonData)) {
      if (jsonData && typeof jsonData === "object") {
        jsonData = [jsonData];
      } else {
        throw new Error("Expected an array or object for JSON input.");
      }
    }
    // Flatten each row
    const flattened = jsonData.map((row) => flattenObject(row));
    // Gather keys for header
    const allKeys = Array.from(
      flattened.reduce((acc, row) => {
        Object.keys(row).forEach((k) => acc.add(k));
        return acc;
      }, new Set())
    );
    // Build CSV
    const csvRows = [];
    csvRows.push(allKeys.join(delimiter)); // header
    for (const row of flattened) {
      csvRows.push(allKeys.map((k) => (row[k] !== undefined ? row[k] : "")).join(delimiter));
    }
    return csvRows.join("\n");
  };

  // Convert
  const handleConvert = () => {
    if (!jsonInput.trim()) {
      setError("No JSON input provided.");
      setCsvOutput("");
      return;
    }
    setProcessing(true);
    setTimeout(() => {
      try {
        const parsed = JSON.parse(jsonInput);
        const result = jsonToCsv(parsed);
        setCsvOutput(result);
        setError("");
        setCopySuccess(false);
      } catch (err) {
        setCsvOutput("");
        setError(`Error: ${err.message}`);
        setCopySuccess(false);
      }
      setProcessing(false);
    }, 500);
  };

  // Clear
  const handleClear = () => {
    setJsonInput("");
    setCsvOutput("");
    setError("");
    setCopySuccess(false);
  };

  // Copy
  const handleCopy = () => {
    navigator.clipboard.writeText(csvOutput);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Export
  const handleExport = () => {
    const blob = new Blob([csvOutput], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "output.csv";
    link.click();
  };

  // Validate CSV
  const handleValidateCsv = () => {
    if (!csvOutput.trim()) {
      alert("No CSV to validate!");
      return;
    }
    const rows = csvOutput.split("\n");
    if (rows.length < 2) {
      alert("CSV seems empty or has no data rows!");
      return;
    }
    const headerCols = rows[0].split(delimiter).length;
    for (let i = 1; i < rows.length; i++) {
      const cols = rows[i].split(delimiter).length;
      if (cols !== headerCols) {
        alert(`Mismatch at row ${i + 1}. Expected ${headerCols} columns, got ${cols}.`);
        return;
      }
    }
    alert("CSV is valid!");
  };

  // Preview
  const handlePreviewCsv = () => {
    if (!csvOutput.trim()) {
      alert("No CSV to preview!");
      return;
    }
    const newWin = window.open("", "_blank", "width=600,height=600");
    newWin.document.write(
      `<pre style="white-space:pre; font-family:monospace;">${csvOutput
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</pre>`
    );
    newWin.document.close();
  };

  // Scroll
  const scrollToTop = () => {
    const outArea = document.getElementById("csv-output-area");
    if (outArea) outArea.scrollTop = 0;
  };
  const scrollToBottom = () => {
    const outArea = document.getElementById("csv-output-area");
    if (outArea) {
      outArea.scrollTop = outArea.scrollHeight;
    }
  };

  // File Upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJsonInput(ev.target.result);
      setError("");
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  // Textarea with line numbers
  const renderTextAreaWithLineNumbers = () => {
    const lines = jsonInput.split("\n");
    return (
      <div style={{ position: "relative", display: "flex", height: "300px" }}>
        {/* line # panel */}
        {showLineNumbers && (
          <div
            style={{
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              borderRight: "1px solid #ccc",
              width: "50px",
              textAlign: "right",
              userSelect: "none",
              color: "#6c757d",
              paddingTop: "5px",
            }}
          >
            {lines.map((_, i) => (
              <div key={i} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                {i + 1}
              </div>
            ))}
          </div>
        )}
        <textarea
          value={jsonInput}
          onChange={(e) => {
            setJsonInput(e.target.value);
            setError("");
          }}
          placeholder="Enter or paste JSON here..."
          style={{
            flex: 1,
            resize: "none",
            borderRadius: 0,
            border: "2px solid #ccc",
            overflowY: "auto",
            fontFamily: "monospace",
            paddingLeft: showLineNumbers ? "10px" : "1em",
          }}
        />
        {/* Upload Icon */}
        <label
          htmlFor="csvJsonFile"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
          }}
        >
          <FaUpload size={20} />
        </label>
        <input
          id="csvJsonFile"
          type="file"
          style={{ display: "none" }}
          accept=".json"
          onChange={handleFileUpload}
        />
      </div>
    );
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "900px" }}>
      {/* Dark Mode Toggle */}
      <div style={{ textAlign: "right", marginBottom: "10px" }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? <FaSun /> : <FaMoon />} {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      {/* Possibly show error/spinner */}
      <div className="text-center mb-3">
        {processing && <div className="alert alert-info">Processing...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        <button
          className="btn btn-info"
          onClick={() => {
            setJsonInput(exampleJson);
            setError("");
          }}
        >
          Use Example Data
        </button>
      </div>

      {/* JSON Input (300px) */}
      <div className="row mb-3">
        <div className="col-md-12">{renderTextAreaWithLineNumbers()}</div>
      </div>

      {/* Convert / Clear / LN / Show Advanced */}
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={handleConvert} disabled={processing}>
          {processing ? "Converting..." : "Convert"}
        </button>
        <button className="btn btn-secondary me-2" onClick={handleClear}>
          Clear
        </button>
        <label className="me-3">
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
          />{" "}
          Show Line Numbers
        </label>
        <button
          className="btn btn-outline-primary"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? "Hide Advanced" : "Show Advanced"}
        </button>
      </div>

      {/* Advanced Panel */}
      {showAdvanced && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "10px",
            backgroundColor: darkMode ? "#444" : "#f8f9fa",
          }}
        >
          <label className="me-3">
            Delimiter:
            <select
              style={{ marginLeft: "5px" }}
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
            >
              <option value=",">Comma (,)</option>
              <option value=";">Semicolon (;)</option>
              <option value="\t">Tab</option>
            </select>
          </label>
          <label className="me-3">
            <input
              type="checkbox"
              checked={ignoreNullFields}
              onChange={(e) => setIgnoreNullFields(e.target.checked)}
            />{" "}
            Ignore Null Fields
          </label>
        </div>
      )}

      {/* CSV Output (300px) */}
      {csvOutput && (
        <div className="mt-4">
          <h5>Converted CSV</h5>
          <div
            id="csv-output-area"
            style={{
              height: "300px",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              border: "2px solid #ccc",
              borderRadius: "5px",
            }}
          >
            <pre
              style={{
                margin: 0,
                padding: "10px",
                whiteSpace: "pre",
                fontFamily: "monospace",
              }}
            >
              {csvOutput}
            </pre>
          </div>
          {/*
            TWO ROWS of Buttons:
            1) Scroll to Top / Bottom
            2) Copy Output, Export CSV, Preview in New Tab, Validate CSV
          */}
          {/* First row: "Scroll to Top" & "Scroll to Bottom" */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button className="btn btn-secondary" onClick={scrollToTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary" onClick={scrollToBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
          </div>

          {/* Second row: Copy Output, Export CSV, Preview, Validate */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"}`}
              onClick={handleCopy}
            >
              <FaCopy /> Copy Output
            </button>
            <button className="btn btn-warning" onClick={handleExport}>
              <FaFileExport /> Export CSV
            </button>
            <button className="btn btn-info" onClick={handlePreviewCsv}>
              Preview in New Tab
            </button>
            <button className="btn btn-info" onClick={handleValidateCsv}>
              Validate CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JsonToCsvConverter;